import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
// eslint-disable-next-line no-restricted-imports
import { AxiosError } from 'axios';
import { Download, X } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { PageHeader, ProLockOverlay } from '../../components/common';
import { usePageTitle } from '../../hooks/usePageTitle';
import { leadService, Lead, updateLeadStatus } from '../../services/leadService';
import { domainService } from '../../services/domainService';
import { chatLogService } from '../../services/chatLogService';
import { api } from '../../services/api';
import { DomainSummary, ApiResponse, ChatMessage } from '../../types';
import { showToast } from '../../stores/uiStore';
import { ROUTES } from '../../config/routes';
import { getErrorMessageByCode } from '../../config/errorMessages';
import { useSubscription } from '../../features/subscription/hooks/useSubscription';

const LIMIT = 20;

const formatDate = (iso: string | undefined): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ja-JP', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const shortDate = (iso: string | undefined): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ja-JP', { month: '2-digit', day: '2-digit' });
  } catch {
    return iso;
  }
};

export const LeadListPage: React.FC = () => {
  usePageTitle('リード一覧');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const leadIdFromUrl = searchParams.get('leadId');
  const { data: subscriptionData } = useSubscription();
  const leadsThisMonth = subscriptionData?.data?.usage?.leads_this_month ?? 0;

  const [domains, setDomains] = useState<DomainSummary[]>([]);
  const [selectedDomainId, setSelectedDomainId] = useState<string>('');
  const [leads, setLeads] = useState<Lead[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [domainsLoading, setDomainsLoading] = useState(true);
  const [isLeadLocked, setIsLeadLocked] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [activeLeadFilter, setActiveLeadFilter] = useState<'all' | 'unresponded' | 'responded'>('all');

  // モーダル state
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [modalMessages, setModalMessages] = useState<ChatMessage[]>([]);
  const [modalLoading, setModalLoading] = useState(false);

  useEffect(() => {
    domainService.getDomains({ limit: 100 }).then((res) => {
      const list = res.data || [];
      setDomains(list as DomainSummary[]);
    }).catch(() => {
      showToast.error('ドメイン一覧の取得に失敗しました');
    }).finally(() => setDomainsLoading(false));
  }, []);

  const fetchLeads = useCallback(async (domainId: string, pageNum: number) => {
    setLoading(true);
    setFetchError(null);
    try {
      const res = await leadService.getLeads(domainId || undefined, { page: pageNum, limit: LIMIT });
      if (res.data) {
        setLeads(res.data.leads);
        setTotal(res.data.total);
      }
    } catch (err: unknown) {
      const status = (err as AxiosError<ApiResponse<unknown>>)?.response?.status;
      const code = (err as AxiosError<ApiResponse<unknown>>)?.response?.data?.error?.code;
      if (code === 'FEATURE_PRO_ONLY') {
        setIsLeadLocked(true);
      } else {
        const errKey = status ? String(status) : 'UNKNOWN';
        setFetchError(getErrorMessageByCode(errKey));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setPage(1);
    fetchLeads(selectedDomainId, 1);
  }, [selectedDomainId, fetchLeads]);

  useEffect(() => {
    if (!leadIdFromUrl || leads.length === 0) return;
    const target = leads.find(l => l.id === leadIdFromUrl);
    if (target) {
      handleLeadClick(target);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadIdFromUrl, leads]);

  useEffect(() => {
    if (selectedDomainId) fetchLeads(selectedDomainId, page);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    if (isLeadLocked) {
      api.post('/subscription/conversion-event', { event_type: 'banner_shown', source: 'leads_lock' }).catch(() => {});
    }
  }, [isLeadLocked]);

  const totalPages = Math.ceil(total / LIMIT);

  const handleLeadClick = async (lead: Lead) => {
    setSelectedLead(lead);
    setModalMessages([]);
    if (!lead.session_id) return;
    setModalLoading(true);
    try {
      const domainId = lead.domain_id || selectedDomainId;
      if (!domainId) return;
      const res = await chatLogService.getChatLogs(domainId, { session_id: lead.session_id, limit: 1 });
      const sessions = res.data?.sessions ?? [];
      if (sessions.length > 0) {
        setModalMessages(sessions[0].messages ?? []);
      }
    } catch {
      // 取得失敗は無視（会話履歴なしで表示）
    } finally {
      setModalLoading(false);
    }
  };

  const closeModal = () => {
    setSelectedLead(null);
    setModalMessages([]);
  };

  const handleStatusToggle = async (lead: Lead) => {
    const newStatus = lead.status === 'responded' ? 'new' : 'responded';
    const domainId = lead.domain_id || selectedDomainId;
    if (!domainId) return;
    await updateLeadStatus(domainId, lead.id, newStatus);
    setLeads(prev => prev.map(l => l.id === lead.id ? { ...l, status: newStatus } : l));
    if (selectedLead?.id === lead.id) {
      setSelectedLead(prev => prev ? { ...prev, status: newStatus } : prev);
    }
  };

  const handleCsvDownload = () => {
    if (isLeadLocked) return;
    if (!leads.length) return;
    const BOM = '﻿';
    const header = ['お名前', 'メールアドレス', '電話番号', '会社名', 'チャットBOT', '獲得日', '状態'];
    const rows = leads.map((l) => [
      l.name,
      l.email,
      l.phone || '',
      l.company || '',
      l.domain_name || '',
      formatDate(l.collected_at),
      l.status === 'responded' ? '対応済み' : '未対応',
    ]);
    const csv =
      BOM +
      [header, ...rows]
        .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        .join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    // eslint-disable-next-line no-restricted-syntax
    a.href = url;
    a.download = `leads_${selectedDomainId}_page${page}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleUpgradeCTA = () => {
    api.post('/subscription/conversion-event', { event_type: 'cta_click', source: 'leads_lock' }).catch(() => {});
    navigate(ROUTES.SUBSCRIPTION);
  };

  const filteredLeads = activeLeadFilter === 'all' ? leads
    : activeLeadFilter === 'responded' ? leads.filter(l => l.status === 'responded')
    : leads.filter(l => l.status !== 'responded');

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageHeader title="リード一覧" subtitle="チャットBOT経由で収集したリード情報です" />

      {/* ドメイン選択 */}
      <div className="mb-4 flex items-center gap-3">
        <label className="text-sm font-medium text-text-secondary" htmlFor="domain-select">
          チャットBOT:
        </label>
        {domainsLoading ? (
          <span className="text-sm text-text-secondary">読み込み中...</span>
        ) : (
          <select
            id="domain-select"
            className="text-sm border border-border rounded-md px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-primary"
            value={selectedDomainId}
            onChange={(e) => setSelectedDomainId(e.target.value)}
          >
            <option value="">全て</option>
            {domains.map((d) => (
              <option key={d.id} value={d.id}>
                {d.domain}
              </option>
            ))}
          </select>
        )}
        {!isLeadLocked && (
          <button
            data-testid="lead-csv-download"
            onClick={handleCsvDownload}
            disabled={!leads.length}
            style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#fff', color: '#0a7a72', border: '1.5px solid #0a7a72', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', fontWeight: 700, cursor: !leads.length ? 'default' : 'pointer', opacity: !leads.length ? 0.4 : 1 }}
          >
            <Download size={14} />
            CSVで書き出す
          </button>
        )}
      </div>

      {/* ロック / 通常表示 */}
      {isLeadLocked ? (
        <div style={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', border: '1px solid #E2E8F0' }}>
          {/* blurテーブル */}
          <div style={{ filter: 'blur(5px)', pointerEvents: 'none', userSelect: 'none' as const, opacity: 0.85 }}>
            <div style={{ background: '#fff', padding: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div style={{ fontSize: '15px', fontWeight: 700 }}>集まったリード</div>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' as const }}>
                <thead>
                  <tr>
                    {['名前', 'メールアドレス', '電話番号', '会社名', 'メッセージ', '収集日時'].map((h) => (
                      <th key={h} style={{ textAlign: 'left', fontSize: '12px', color: '#94A3B8', fontWeight: 700, padding: '10px 14px', borderBottom: '1px solid #E2E8F0' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <tr key={i}>
                      {['———', '———@example.com', '090-xxxx-xxxx', '株式会社———', '———について', '—/— —:—'].map((v, j) => (
                        <td key={j} style={{ fontSize: '13px', color: '#334155', padding: '14px', borderBottom: '1px solid #F1F5F9' }}>{v}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <ProLockOverlay
            variant="inline-overlay"
            title={<>リード一覧の閲覧は<span style={{ color: '#0a7a72' }}>Proプラン</span>から</>}
            description={`今月${leadsThisMonth}件のリードが集まっています。Proプランにアップグレードすると、一覧での閲覧・CSV書き出し・お客様への対応ができるようになります。`}
            onUpgrade={handleUpgradeCTA}
          />
        </div>
      ) : (
        /* 通常テーブル */
        <>
          {/* フィルタータブ */}
          <div style={{ display: 'flex', gap: '6px', marginBottom: '16px' }}>
            {([
              { key: 'all', label: 'すべて' },
              { key: 'unresponded', label: '未対応' },
              { key: 'responded', label: '対応済み' },
            ] as const).map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setActiveLeadFilter(key)}
                style={{
                  padding: '5px 14px',
                  borderRadius: '999px',
                  fontSize: '13px',
                  fontWeight: key === activeLeadFilter ? 700 : 400,
                  border: '1.5px solid',
                  borderColor: key === activeLeadFilter ? '#0a7a72' : '#E2E8F0',
                  background: key === activeLeadFilter ? '#0a7a72' : '#fff',
                  color: key === activeLeadFilter ? '#fff' : '#475569',
                  cursor: 'pointer',
                }}
              >
                {label}
              </button>
            ))}
          </div>

          <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #E2E8F0', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table data-testid="lead-list-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #E2E8F0', background: '#F8FAFC' }}>
                    {['お名前', 'メールアドレス', '電話番号', '会社名', 'チャットBOT', '獲得日', '状態'].map(h => (
                      <th key={h} style={{ textAlign: 'left', fontSize: '12px', fontWeight: 700, color: '#94A3B8', padding: '12px 16px', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: '48px', fontSize: '13px', color: '#94A3B8' }}>読み込み中...</td></tr>
                  ) : fetchError ? (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: '48px' }}>
                      <div style={{ fontSize: '13px', color: '#94A3B8', marginBottom: '12px' }}>{fetchError}</div>
                      <button
                        data-testid="error-retry-button"
                        onClick={() => fetchLeads(selectedDomainId, page)}
                        style={{ fontSize: '13px', padding: '8px 16px', border: '1px solid #E2E8F0', borderRadius: '8px', background: '#fff', cursor: 'pointer' }}
                      >
                        再試行
                      </button>
                    </td></tr>
                  ) : filteredLeads.length === 0 ? (
                    <tr>
                      <td colSpan={7} data-testid="lead-empty-state" style={{ padding: 0 }}>
                        <div style={{ padding: '64px 32px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                          <div style={{ width: '72px', height: '72px', borderRadius: '18px', background: '#E0F2F1', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#0a7a72" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                            </svg>
                          </div>
                          <div style={{ fontSize: '18px', fontWeight: 700, color: '#0F172A', marginBottom: '12px' }}>
                            まだリードがありません
                          </div>
                          <div style={{ fontSize: '14px', color: '#94A3B8', lineHeight: 1.7, maxWidth: '380px', marginBottom: '24px' }}>
                            チャットボットが会話の中でお客様の連絡先を獲得すると、ここに一覧で表示されます。まずはボットをサイトに設置しましょう。
                          </div>
                          <button
                            onClick={() => navigate(ROUTES.DOMAINS)}
                            style={{ background: '#0a7a72', color: '#fff', border: 'none', borderRadius: '10px', padding: '12px 24px', fontSize: '15px', fontWeight: 700, cursor: 'pointer' }}
                          >
                            チャットボットを確認
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredLeads.map((lead) => (
                        <tr key={lead.id} onClick={() => handleLeadClick(lead)} style={{ borderBottom: '1px solid #F1F5F9', cursor: 'pointer' }}>
                          <td style={{ padding: '14px 16px', fontSize: '14px', fontWeight: 700, color: '#0F172A' }}>{lead.name}</td>
                          <td style={{ padding: '14px 16px', fontSize: '13px', color: '#64748B' }}>{lead.email}</td>
                          <td style={{ padding: '14px 16px', fontSize: '13px', color: '#475569', whiteSpace: 'nowrap' }}>{lead.phone || '—'}</td>
                          <td style={{ padding: '14px 16px', fontSize: '13px', color: '#64748B', whiteSpace: 'nowrap' }}>{lead.company || '—'}</td>
                          <td style={{ padding: '14px 16px', fontSize: '13px', color: '#64748B', whiteSpace: 'nowrap' }}>{lead.domain_name || '—'}</td>
                          <td style={{ padding: '14px 16px', fontSize: '13px', color: '#94A3B8', whiteSpace: 'nowrap' }}>{shortDate(lead.collected_at)}</td>
                          <td style={{ padding: '14px 16px' }}>
                            <button
                              data-testid={'lead-status-toggle-' + lead.id}
                              onClick={(e) => { e.stopPropagation(); handleStatusToggle(lead); }}
                              style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 700, border: 'none', cursor: 'pointer', ...(lead.status === 'responded' ? { background: '#DCFCE7', color: '#166534' } : { background: '#FEF3C7', color: '#92400E' }) }}
                            >
                              {lead.status === 'responded' ? '対応済み' : '未対応'}
                            </button>
                          </td>
                        </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* ページネーション */}
            {!loading && !fetchError && total > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderTop: '1px solid #F1F5F9', fontSize: '13px', color: '#64748B' }}>
                <span>全{total}件中 {(page - 1) * LIMIT + 1}-{Math.min(page * LIMIT, total)}件を表示</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1 || loading}
                    style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', background: '#fff', color: '#475569', fontSize: '13px', cursor: page <= 1 ? 'default' : 'pointer', opacity: page <= 1 ? 0.4 : 1 }}
                  >
                    前へ
                  </button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    const p = i + 1;
                    return (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid', borderColor: p === page ? '#0a7a72' : '#E2E8F0', background: p === page ? '#0a7a72' : '#fff', color: p === page ? '#fff' : '#475569', fontSize: '13px', fontWeight: p === page ? 700 : 400, cursor: 'pointer' }}
                      >
                        {p}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages || loading}
                    style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', background: '#fff', color: '#475569', fontSize: '13px', cursor: page >= totalPages ? 'default' : 'pointer', opacity: page >= totalPages ? 0.4 : 1 }}
                  >
                    次へ
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* リード詳細モーダル */}
      {selectedLead && (
        <div
          data-testid="lead-detail-modal-overlay"
          onClick={closeModal}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
        >
          <div
            data-testid="lead-detail-modal"
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '640px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          >
            {/* ヘッダー */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #E2E8F0' }}>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#0F172A' }}>{selectedLead.name}</div>
              <button
                data-testid="lead-modal-close"
                onClick={closeModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: '4px', display: 'flex', alignItems: 'center' }}
              >
                <X size={20} />
              </button>
            </div>

            {/* リード情報 */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #F1F5F9', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px' }}>
              <div>
                <div style={{ fontSize: '11px', color: '#94A3B8', fontWeight: 700, marginBottom: '2px' }}>メールアドレス</div>
                <div style={{ fontSize: '13px', color: '#334155' }}>{selectedLead.email}</div>
              </div>
              {selectedLead.phone && (
                <div>
                  <div style={{ fontSize: '11px', color: '#94A3B8', fontWeight: 700, marginBottom: '2px' }}>電話番号</div>
                  <div style={{ fontSize: '13px', color: '#334155' }}>{selectedLead.phone}</div>
                </div>
              )}
              {selectedLead.company && (
                <div>
                  <div style={{ fontSize: '11px', color: '#94A3B8', fontWeight: 700, marginBottom: '2px' }}>会社名</div>
                  <div style={{ fontSize: '13px', color: '#334155' }}>{selectedLead.company}</div>
                </div>
              )}
              <div>
                <div style={{ fontSize: '11px', color: '#94A3B8', fontWeight: 700, marginBottom: '2px' }}>獲得日</div>
                <div style={{ fontSize: '13px', color: '#334155' }}>{formatDate(selectedLead.collected_at)}</div>
              </div>
              <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ fontSize: '11px', color: '#94A3B8', fontWeight: 700 }}>状態:</div>
                <button
                  data-testid="lead-modal-status-toggle"
                  onClick={(e) => { e.stopPropagation(); handleStatusToggle(selectedLead); }}
                  style={{ padding: '4px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 700, border: 'none', cursor: 'pointer', ...(selectedLead.status === 'responded' ? { background: '#DCFCE7', color: '#166534' } : { background: '#FEF3C7', color: '#92400E' }) }}
                >
                  {selectedLead.status === 'responded' ? '対応済み' : '未対応'}
                </button>
              </div>
            </div>

            {/* 会話履歴 */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
              {modalLoading ? (
                <div style={{ textAlign: 'center', padding: '32px', fontSize: '13px', color: '#94A3B8' }}>読み込み中...</div>
              ) : modalMessages.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px', fontSize: '13px', color: '#94A3B8' }}>
                  {selectedLead.session_id ? '会話履歴がありません' : '会話IDが紐付いていません'}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {modalMessages.map((msg, idx) => (
                    <div
                      key={idx}
                      style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}
                    >
                      <div
                        style={{
                          maxWidth: '75%',
                          padding: '10px 14px',
                          borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                          background: msg.role === 'user' ? '#0a7a72' : '#F1F5F9',
                          color: msg.role === 'user' ? '#fff' : '#334155',
                          fontSize: '13px',
                          lineHeight: 1.6,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {msg.role === 'user' ? msg.content : (
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                            p: ({children})=><p className="mb-1 last:mb-0">{children}</p>,
                            ul: ({children})=><ul className="list-disc pl-4 mb-1">{children}</ul>,
                            ol: ({children})=><ol className="list-decimal pl-4 mb-1">{children}</ol>,
                            li: ({children})=><li className="mb-0.5">{children}</li>,
                            strong: ({children})=><strong className="font-semibold">{children}</strong>,
                            h1: ({children})=><h1 className="font-bold text-sm mt-1 mb-0.5">{children}</h1>,
                            h2: ({children})=><h2 className="font-bold text-sm mt-1 mb-0.5">{children}</h2>,
                            h3: ({children})=><h3 className="font-semibold text-sm mt-1 mb-0.5">{children}</h3>,
                            code: ({children})=><code className="bg-gray-100 rounded px-0.5 text-xs font-mono">{children}</code>,
                            pre: ({children})=><pre className="bg-gray-100 rounded p-2 text-xs overflow-auto my-1">{children}</pre>,
                            table: ({children})=><table className="border-collapse text-xs my-1 w-full">{children}</table>,
                            th: ({children})=><th className="border border-gray-300 px-1.5 py-0.5 bg-gray-100 font-semibold">{children}</th>,
                            td: ({children})=><td className="border border-gray-300 px-1.5 py-0.5">{children}</td>,
                          }}>{msg.content}</ReactMarkdown>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
